import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input, Button } from 'antd';
import { Send, Copy, Check, Bot, User, Sparkles } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import PageAnimationWrapper from '../../components/PageAnimationWrapper';
import toast from 'react-hot-toast';
import { SERVER_URL } from '../../utils/serverConfig';

// Logo Icon Component for Bot Avatar - Circular arrangement of 6 figures
const LogoIcon = ({ size = 40, className = '' }) => {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="150 60 500 500" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Green Figure - Top */}
      <path fillRule="evenodd" clipRule="evenodd" d="M358.223 255.521C346.35 236.916 318.205 154.654 314.654 145.22C310.996 130.114 315.29 119.142 327.428 112.357C334.848 108.912 341.527 108.435 347.357 110.979C373.329 120.838 392.357 128.152 404.495 133.082C414.46 137.11 420.237 147.446 419.442 158.1C409.531 214.708 404.601 243.012 404.601 243.012C402.322 252.765 397.658 259.072 390.714 261.934C380.643 267.076 364.583 265.486 358.223 255.521Z" fill="#34A851"/>
      <circle cx="378.945" cy="62.3744" r="20" fill="#34A851"/>
      
      {/* Orange Figure - Top Right */}
      <path fillRule="evenodd" clipRule="evenodd" d="M429.671 263.895C441.014 244.973 502.181 183.17 509.018 175.75C520.944 165.732 532.658 164.672 544.107 172.516C550.52 177.658 553.859 183.382 554.231 189.743C556.987 217.358 558.895 237.658 559.902 250.697C560.75 261.404 554.072 271.21 544.16 275.238C489.089 291.563 461.527 299.726 461.527 299.726C451.774 302.005 444.035 300.68 438.417 295.75C429.3 289.125 423.629 274.019 429.671 263.895Z" fill="#F2673A"/>
      <circle cx="611.844" cy="196.368" r="15" fill="#F2673A"/>
      
      {/* Dark Blue Figure - Bottom Right */}
      <path fillRule="evenodd" clipRule="evenodd" d="M458.19 330.786C480.239 330.839 564.727 351.511 574.533 353.631C589.215 358.719 596.159 368.207 595.311 382.094C594.198 390.203 591.017 396.034 585.717 399.585C563.455 416.122 546.971 428.207 536.317 435.734C527.572 441.935 515.752 441.246 507.218 434.833C464.921 395.981 443.773 376.528 443.773 376.528C436.776 369.32 433.967 362.005 435.292 354.638C436.352 343.454 446.37 330.786 458.19 330.786Z" fill="#366EB6"/>
      <circle cx="609.676" cy="452.43" r="15" fill="#366EB6"/>
      
      {/* Purple Figure - Bottom */}
      <path fillRule="evenodd" clipRule="evenodd" d="M415.043 391.741C426.545 410.557 452.941 493.455 456.281 502.943C459.62 518.155 455.114 529.021 442.871 535.54C435.344 538.826 428.718 539.144 422.941 536.494C397.181 526.105 378.312 518.367 366.28 513.226C356.421 508.985 350.856 498.543 351.863 487.943C362.941 431.547 368.453 403.401 368.453 403.401C370.944 393.702 375.715 387.447 382.711 384.744C392.888 379.709 408.895 381.67 415.043 391.741Z" fill="#6C59A6"/>
      <circle cx="390.346" cy="584.463" r="15" fill="#6C59A6"/>
      
      {/* Light Blue Figure - Bottom Left */}
      <path fillRule="evenodd" clipRule="evenodd" d="M339.246 383.684C328.646 403.031 269.97 467.218 263.398 474.851C251.896 485.293 240.235 486.83 228.468 479.462C221.896 474.586 218.292 468.967 217.709 462.66C213.892 435.204 211.189 414.956 209.652 401.97C208.38 391.317 214.687 381.246 224.387 376.847C278.769 358.348 305.96 349.126 305.96 349.126C315.607 346.475 323.398 347.482 329.229 352.2C338.61 358.454 344.918 373.348 339.246 383.684Z" fill="#469FD8"/>
      <circle cx="159.882" cy="458.26" r="15" fill="#469FD8"/>
      
      {/* Teal Figure - Top Left */}
      <path fillRule="evenodd" clipRule="evenodd" d="M303.416 318.542C281.419 317.482 197.991 292.782 188.239 290.185C173.822 284.408 167.302 274.602 168.839 260.768C170.323 252.711 173.822 247.04 179.281 243.701C202.338 228.224 219.352 216.987 230.377 209.937C239.387 204.16 251.207 205.432 259.423 212.216C299.812 253.029 320.059 273.489 320.059 273.489C326.685 281.015 329.176 288.436 327.48 295.75C325.837 306.934 315.236 319.125 303.416 318.542Z" fill="#2DBDB8"/>
      <circle cx="157.923" cy="189.849" r="15" fill="#2DBDB8"/>
    </svg>
  );
};

const { TextArea } = Input;

const ChatWithUs = () => {
  const { mode } = useTheme();
  const { user } = useAuth();
  const isDark = mode === 'dark';
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [messageActionButtons, setMessageActionButtons] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Predefined suggestions
  const suggestions = [
    "How productive was I this week?",
    "Show me my productivity stats for the last 7 days",
    "What were my most productive hours yesterday?",
    "How much time did I spend on projects today?",
  ];

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Call Claude API with streaming
  const streamChatResponse = async (userMessage) => {
    // Declare variables outside try block so they're accessible in catch
    const botMessageId = Date.now() + 1;
    let botMessageCreated = false;
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Prepare conversation history
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
      }));

      const response = await fetch(`${SERVER_URL}/api/v1/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: userMessage,
          conversationHistory: conversationHistory,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        // Decode the chunk
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'text') {
                // Create bot message on first text content
                if (!botMessageCreated && data.content) {
                  botMessageCreated = true;
                  const botMessage = {
                    id: botMessageId,
                    text: data.content,
                    sender: 'bot',
                    timestamp: new Date(),
                    isStreaming: true,
                  };
                  setMessages((prev) => [...prev, botMessage]);
                } else {
                  // Append text to existing bot message
                  setMessages((prev) => 
                    prev.map(msg => 
                      msg.id === botMessageId 
                        ? { ...msg, text: msg.text + data.content }
                        : msg
                    )
                  );
                }
              } else if (data.type === 'tool_start') {
                // Don't show tool analysis messages in chat - just skip them
                // Tool usage is internal and shouldn't be displayed to users
              } else if (data.type === 'done') {
                // Mark streaming as complete
                setMessages((prev) => 
                  prev.map(msg => 
                    msg.id === botMessageId 
                      ? { ...msg, isStreaming: false }
                      : msg
                  )
                );
                setIsLoading(false);
              } else if (data.type === 'action_buttons' && data.buttons) {
                // Store action buttons for this message
                setMessageActionButtons(prev => ({
                  ...prev,
                  [botMessageId]: data.buttons
                }));
              } else if (data.type === 'error') {
                toast.error(data.message || 'An error occurred');
                // Create error message if bot message doesn't exist
                if (!botMessageCreated) {
                  botMessageCreated = true;
                  const botMessage = {
                    id: botMessageId,
                    text: 'Sorry, I encountered an error processing your request.',
                    sender: 'bot',
                    timestamp: new Date(),
                    isStreaming: false,
                  };
                  setMessages((prev) => [...prev, botMessage]);
                } else {
                  setMessages((prev) => 
                    prev.map(msg => 
                      msg.id === botMessageId 
                        ? { ...msg, text: msg.text || 'Sorry, I encountered an error processing your request.', isStreaming: false }
                        : msg
                    )
                  );
                }
                setIsLoading(false);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error streaming chat response:', error);
      toast.error('Failed to get response. Please try again.');
      
      // Create or update bot message with error
      if (!botMessageCreated) {
        const botMessage = {
          id: botMessageId,
          text: 'Sorry, I encountered an error. Please try again.',
          sender: 'bot',
          timestamp: new Date(),
          isStreaming: false,
        };
        setMessages((prev) => [...prev, botMessage]);
      } else {
        setMessages((prev) => 
          prev.map(msg => 
            msg.sender === 'bot' && msg.isStreaming
              ? { ...msg, text: 'Sorry, I encountered an error. Please try again.', isStreaming: false }
              : msg
          )
        );
      }
      
      setIsLoading(false);
    }
  };

  // Handler for action button click - triggers detailed analysis
  const handleActionButtonClick = async (query, messageId) => {
    // Add as user message
    const actionMessage = {
      id: Date.now(),
      text: query,
      sender: 'user',
      timestamp: new Date(),
    };
    
    setMessages((prev) => [...prev, actionMessage]);
    setIsLoading(true);
    
    // Scroll to bottom
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    
    // Trigger detailed analysis
    await streamChatResponse(query);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageText = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    // Call streaming API
    await streamChatResponse(messageText);
  };

  const handleSuggestionClick = async (suggestion) => {
    if (isLoading) return;
    
    const userMessage = {
      id: Date.now(),
      text: suggestion,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    // Call streaming API
    await streamChatResponse(suggestion);
  };

  const handleCopyMessage = (messageId, text) => {
    navigator.clipboard.writeText(text);
    setCopiedMessageId(messageId);
    toast.success('Message copied to clipboard');
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const messageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut'
      }
    },
  };

  const suggestionVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
        ease: 'easeOut'
      }
    }),
    hover: {
      scale: 1.02,
      transition: { duration: 0.2 }
    }
  };

  return (
    <PageAnimationWrapper className={`transition-all duration-300 ${
      isDark ? 'bg-black' : 'bg-white'
    }`}>
      <style>{`
        .chat-input textarea {
          padding: 14px 18px !important;
          font-size: 15px !important;
          line-height: 1.6 !important;
          border-radius: 12px !important;
          min-height: 50px !important;
        }
        .chat-input.ant-input {
          border-radius: 12px !important;
        }
        .chat-container {
          max-width: 1000px;
          margin: 0 auto;
          width: 100%;
        }
      `}</style>
      
      <div className="flex flex-col w-full" style={{ height: 'calc(100vh - 60px)', minHeight: '700px' }}>
        {/* Messages Area - Integrated Chat */}
        <div className={`flex-1 overflow-y-auto w-full ${
          isDark 
            ? 'bg-black' 
            : 'bg-white'
        }`}>
          <div className="chat-container px-6 py-8">
            <AnimatePresence>
              {messages.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col items-center justify-center min-h-[70vh] text-center"
                >
                  <div className={`mb-8 p-5 rounded-full ${
                    isDark ? 'bg-white/5' : 'bg-gray-100'
                  }`}>
                    <Sparkles className={`h-14 w-14 ${
                      isDark ? 'text-white' : 'text-gray-700'
                    }`} />
                  </div>
                  <h1 className={`text-4xl font-semibold mb-4 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    How can I help you today?
                  </h1>
                </motion.div>
              ) : (
                <div className="space-y-8">
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      variants={messageVariants}
                      initial="hidden"
                      animate="visible"
                      className={`flex gap-5 ${
                        message.sender === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      {message.sender === 'bot' && (
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${
                          isDark ? 'bg-transparent' : 'bg-transparent'
                        }`}>
                          <LogoIcon size={40} className="w-full h-full" />
                        </div>
                      )}
                      
                      <div className={`flex flex-col ${
                        message.sender === 'user' ? 'items-end max-w-[80%]' : 'items-start max-w-[80%]'
                      }`}>
                        <div className={`rounded-2xl px-5 py-4 transition-all ${
                          message.sender === 'user'
                            ? isDark
                              ? 'bg-blue-600 !text-white hover:bg-blue-500'
                              : 'bg-blue-500 !text-white hover:bg-blue-600'
                            : isDark
                            ? 'bg-white/5 text-gray-200 hover:bg-white/8'
                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                        }`}>
                          <div 
                            className={`text-[16px] leading-relaxed whitespace-pre-wrap break-words ${
                              message.sender === 'user' ? ' !text-white' : ''
                            }`}
                            dangerouslySetInnerHTML={{
                              __html: message.text
                                .replace(/^###?\s+/gm, '') // Remove ## and ### headers
                                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                .replace(/\n/g, '<br />')
                            }}
                          />
                        </div>
                        
                        {message.sender === 'bot' && (
                          <div className="flex flex-col gap-2 mt-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleCopyMessage(message.id, message.text)}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  isDark 
                                    ? 'text-gray-500 hover:text-gray-300 hover:bg-white/5' 
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                }`}
                                title="Copy message"
                              >
                                {copiedMessageId === message.id ? (
                                  <Check className="h-4 w-4" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                            
                            {/* Action Buttons */}
                            {messageActionButtons[message.id] && messageActionButtons[message.id].length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {messageActionButtons[message.id].map((button, index) => (
                                  <button
                                    key={index}
                                    onClick={() => handleActionButtonClick(button.query, message.id)}
                                    disabled={isLoading}
                                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                                      isDark
                                        ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30 hover:border-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed'
                                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed'
                                    }`}
                                  >
                                    <Sparkles className="h-4 w-4" />
                                    {button.label}
                                    <span className="text-xs opacity-70">→</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {message.sender === 'user' && (
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                          isDark ? 'bg-blue-600' : 'bg-blue-500'
                        }`}>
                          <User className="h-6 w-6 !text-white" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </AnimatePresence>

            {/* Typing Indicator */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex gap-5 justify-start"
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${
                  isDark ? 'bg-transparent' : 'bg-transparent'
                }`}>
                  <LogoIcon size={40} className="w-full h-full" />
                </div>
                <div className={`rounded-2xl px-5 py-4 ${
                  isDark ? 'bg-white/5' : 'bg-gray-100'
                }`}>
                  <div className="flex space-x-1.5">
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className={`w-2 h-2 rounded-full ${
                          isDark ? 'bg-white/40' : 'bg-gray-400'
                        }`}
                        animate={{
                          y: [0, -6, 0],
                        }}
                        transition={{
                          duration: 0.6,
                          repeat: Infinity,
                          delay: i * 0.2,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} className="h-6" />
          </div>
        </div>

        {/* Input Area - Fixed at Bottom - Integrated */}
        <div className={`${
          isDark 
            ? 'bg-black border-t border-white/5' 
            : 'bg-white border-t border-gray-100'
        }`}>
          <div className="chat-container px-6 py-4">
            {/* Input Field - Pehle input field */}
            <div className={`flex items-center gap-3 p-4 rounded-xl mb-3 transition-all ${
              isDark 
                ? 'bg-white/5 border border-white/10 hover:border-white/20 focus-within:border-white/20' 
                : 'bg-gray-50 border border-gray-200 hover:border-gray-300 focus-within:border-gray-300'
            }`}>
              <div className="flex-1">
                <TextArea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Message TeamGrid..."
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  disabled={isLoading}
                  className={`chat-input border-0 shadow-none ${
                    isDark
                      ? '!bg-transparent !text-white placeholder:!text-gray-500'
                      : '!bg-transparent !text-gray-900 placeholder:!text-gray-400'
                  }`}
                  style={{
                    resize: 'none',
                  }}
                />
              </div>
              <Button
                type="text"
                icon={<Send className="h-4 w-4" />}
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                loading={isLoading}
                className={`rounded-lg border-0 flex-shrink-0 transition-all ${
                  isDark
                    ? 'text-gray-400 hover:text-white hover:bg-white/10 disabled:text-gray-600 disabled:hover:bg-transparent'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent'
                }`}
                style={{
                  height: '40px',
                  minWidth: '40px',
                  padding: '0 12px',
                }}
              />
            </div>

            {/* Suggestions - Ab input ke NICHE */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-2 flex flex-wrap gap-2"
            >
              {suggestions.map((suggestion, index) => (
                <motion.button
                  key={index}
                  custom={index}
                  variants={suggestionVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                    isDark
                      ? 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/5 hover:border-white/10'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {suggestion}
                </motion.button>
              ))}
            </motion.div>
            
            <p className={`text-xs text-center mt-2 ${
              isDark ? 'text-gray-500' : 'text-gray-400'
            }`}>
              TeamGrid is designed to assist your work, but we recommend double-checking important information for accuracy
            </p>
          </div>
        </div>
      </div>
    </PageAnimationWrapper>
  );
};

export default ChatWithUs;
